import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import {
  useAccountStats,
  AccountStats,
} from '../../../hooks/api/useAccountStats';
import dayjs, { Dayjs } from 'dayjs';
import { ModalDialog } from '../../../components/ModalDialog';
import { StyledInputLabel } from '../../../components/inputs/InputLabel';
import { AlertToast } from '../../../components/AlertToast';

export type EditAccountDialogProps = {
  open: boolean;
  onClose: () => void;
  account: AccountStats | null;
  onSuccess?: () => void;
};

const EditAccountDialog = ({
  open,
  onClose,
  account,
  onSuccess,
}: EditAccountDialogProps) => {
  const { updateAccount } = useAccountStats();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [contractExpirationDate, setContractExpirationDate] =
    useState<Dayjs | null>(null);
  const [trial, setTrial] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (account) {
      setName(account.name || '');
      setContractExpirationDate(
        account.contract_expiration_date
          ? dayjs(account.contract_expiration_date)
          : null
      );
      setTrial(!!account.trial);
    }
  }, [account]);

  const handleSubmit = async () => {
    setNameErrorMessage('');

    const newNameErrorMessage = !name ? 'Please enter account name' : '';
    setNameErrorMessage(newNameErrorMessage);

    if (newNameErrorMessage) {
      return;
    }

    if (account && name) {
      // Send only what changed
      const changes: Partial<AccountStats> = {};
      if (name !== account.name) {
        changes.name = name;
      }
      const date = contractExpirationDate?.format('YYYY-MM-DD');
      const savedDate = account.contract_expiration_date
        ? dayjs(account.contract_expiration_date).format('YYYY-MM-DD')
        : undefined;
      if (date && date !== savedDate) {
        changes.contract_expiration_date = date;
      }
      if (trial !== !!account.trial) {
        changes.trial = trial;
      }
      if (Object.keys(changes).length === 0) {
        handleClose();
        return;
      }

      try {
        await updateAccount({ path: account._id, data: changes });

        onClose();
        setShowToast(true);
        if (onSuccess) {
          onSuccess();
        }
        setTimeout(() => {
          setShowToast(false);
        }, 1600);
      } catch (error) {
        console.error('Error updating account:', error);
        setError(true);
        setTimeout(() => {
          setError(false);
        }, 1600);
      }
    }
  };

  const handleClose = () => {
    onClose();
    setNameErrorMessage('');
  };

  if (!account) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ModalDialog
        open={open}
        onClose={handleClose}
        title={t('accounts.editAccountDialog.title', 'Edit Account')}
        subtitle={
          t(
            'accounts.editAccountDialog.subtext',
            'Update account information'
          ) as string
        }
        maxWidth="md"
        actions={
          <>
            <Button variant="default" onClick={handleClose}>
              {t('accounts.editAccountDialog.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSubmit} variant="primary">
              {t('accounts.editAccountDialog.save', 'Save')}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Box>
            <StyledInputLabel htmlFor="name">
              {t('accounts.editAccountDialog.name', 'Account Name')}{' '}
              <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <TextField
              required
              id="name"
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!nameErrorMessage}
              helperText={nameErrorMessage}
              placeholder={String(
                t('accounts.editAccountDialog.name', 'Account Name')
              )}
            />
          </Box>

          <Box>
            <StyledInputLabel htmlFor="path">
              {t('accounts.editAccountDialog.path', 'Account Path')}
            </StyledInputLabel>
            <TextField
              id="path"
              type="text"
              fullWidth
              value={account._id}
              disabled
              placeholder={String(
                t('accounts.editAccountDialog.path', 'Account Path')
              )}
            />
          </Box>

          <Box>
            <StyledInputLabel htmlFor="contract_expiration_date">
              {t(
                'accounts.editAccountDialog.contractExpiration',
                'Contract Expiration Date'
              )}
            </StyledInputLabel>
            <DatePicker
              value={contractExpirationDate}
              onChange={(newValue) => setContractExpirationDate(newValue)}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  id: 'contract_expiration_date',
                  fullWidth: true,
                  placeholder: 'YYYY-MM-DD',
                },
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={trial}
                onChange={(e) => setTrial(e.target.checked)}
              />
            }
            label={t('accounts.trialAccount')}
          />
        </Box>
      </ModalDialog>
      {showToast && (
        <AlertToast severity="success">
          {t(
            'accounts.editAccountDialog.success',
            'Account updated successfully'
          )}
        </AlertToast>
      )}
      {error && (
        <AlertToast severity="error">
          {t('accounts.editAccountDialog.error', 'Error updating account')}
        </AlertToast>
      )}
    </LocalizationProvider>
  );
};

export default EditAccountDialog;
